import Yup from 'yup';

const companyInvestmentMandate = Yup.object().shape({
  assets: Yup.array().ensure(),
  sectors: Yup.array().ensure(),
  regions: Yup.array().ensure()
});

const positionInvestmentMandate = Yup.object().shape({
  assets: Yup.array().ensure(),
  sectors: Yup.array().ensure(),
  regions: Yup.array().ensure()
});

const minimum = Yup.object().shape({
  yearsOfExperienceMin: Yup.number().nullable(),
  yearsOfExperienceMax: Yup.number().nullable(),
  firmExperience: Yup.array().ensure(),
  degreesAndCertifications: Yup.array().ensure(),
  gpa: Yup.object().shape({
    max: Yup.string(),
    score: Yup.number()
      .min(0, 'Please enter a value greater than 0')
      .when('max', {
        is: val => Number(val) === 4,
        then: Yup.number().max(4, 'Please enter a value less than or equal to 4.0'),
        otherwise: Yup.number().max(100, 'Please enter a value less than or equal to 100')
      })
      .transform(value => (Number.isNaN(value) ? null : value))
      .nullable()
  }),
  sat: Yup.object().shape({
    max: Yup.string(),
    score: Yup.number()
      .min(0, 'Please enter a value greater than 0')
      .when('max', {
        is: val => Number(val) === 1600,
        then: Yup.number().max(1600, 'Please enter a value less than or equal to 1600'),
        otherwise: Yup.number().max(2400, 'Please enter a value less than or equal to 2400')
      })
      .integer('Please enter a whole number')
      .transform(value => (Number.isNaN(value) ? null : value))
      .nullable()
  }),
  act: Yup.object().shape({
    score: Yup.number()
      .min(0, 'Please enter a value greater than 0')
      .max(36, 'Please enter a value less than or equal to 36')
      .integer('Please enter a whole number')
      .transform(value => (Number.isNaN(value) ? null : value))
      .nullable()
  })
});

export const generateValidationSchema = currentUser => {
  const {
    mostRecentEmployer: { employer }
  } = currentUser;
  const { name } = employer;

  const regex = new RegExp(`${name}`, 'i');

  const employerDetails = Yup.object().shape({
    aum: Yup.number()
      .required('This field is required')
      .transform(value => (Number.isNaN(value) ? null : value))
      .nullable(),
    fundType: Yup.string()
      .required('This field is required')
      .nullable(),
    strategy: Yup.string()
      .required('This field is required')
      .nullable(),
    city: Yup.string()
      .required('This field is required')
      .nullable(),
    description: Yup.string()
      .required('This field is required')
      .test({
        test: value => !value.match(regex),
        message: 'Your company name is not permitted in the company description'
      })
      .nullable()
  });

  const positionDetails = Yup.object().shape({
    city: Yup.string()
      .required('This field is required')
      .nullable(),
    title: Yup.string()
      .required('This field is required')
      .nullable(),
    description: Yup.string()
      .required('This field is required')
      .test({
        test: value => !value.match(regex),
        message: 'Your company name is not permitted in the position details'
      })
  });

  return Yup.object().shape({
    employerDetails,
    companyInvestmentMandate,
    useCompanyInvestmentMandate: Yup.boolean().required('This field is required'),
    positionInvestmentMandate,
    minimum,
    coverLetter: Yup.string().required('This field is required'),
    positionDetails
  });
};

export const getInitialFormValues = currentUser => {
  const { employer } = currentUser.mostRecentEmployer;
  const { aum, fundType, strategy, description, city, assets, sectors, regions } = employer;

  return {
    employerDetails: {
      aum,
      fundType,
      strategy,
      city,
      description: description || ''
    },
    companyInvestmentMandate: {
      assets,
      sectors,
      regions
    },
    useCompanyInvestmentMandate: true,
    positionInvestmentMandate: {
      assets: [],
      sectors: [],
      regions: []
    },
    minimum: {
      yearsOfExperienceMin: null,
      yearsOfExperienceMax: null,
      firmExperience: [],
      degreesAndCertifications: [],
      gpa: {
        score: null,
        max: '4.0'
      },
      sat: {
        score: null,
        max: '1600'
      },
      act: {
        score: null,
        max: 36
      }
    },
    coverLetter: 'optional',
    positionDetails: {
      city: employer.city,
      title: null,
      description: ''
    }
  };
};
